import type {
  CustomerChannelSplit,
  CustomerChannelStat,
  CustomerInsights,
} from '../../../types/customer'

// Parses the additive Behavior-tab fields out of the insights payload we ALREADY
// fetch (`getInsights` on cova + greenline) — no new endpoint, no backend change.
// Shared by both gateways so the (undocumented) wire-shape tolerance lives in one
// unit-tested place.
//
// Every reader is intentionally defensive: unknown/missing/mis-typed fields
// resolve to `undefined`, so a slightly-wrong shape assumption degrades to a
// dimmed sample value in the UI instead of a wrong real number. The audit
// (`documents/customer-profile-metrics-audit.html`) cites the source keys:
//   completed_order_last_12_months.{online_orders,pos_orders}.{number_orders,total_amount_spent}
//   average_comparison · orders_last_30_days

export type CustomerInsightsExtras = Pick<
  CustomerInsights,
  'ordersLast30Days' | 'channelSplit' | 'averageComparison'
>

const asRecord = (v: unknown): Record<string, unknown> | undefined =>
  v && typeof v === 'object' && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : undefined

const numOrUndef = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? v : undefined

const firstNum = (
  obj: Record<string, unknown> | undefined,
  keys: string[],
): number | undefined => {
  if (!obj) return undefined
  for (const k of keys) {
    const n = numOrUndef(obj[k])
    if (n !== undefined) return n
  }
  return undefined
}

const mapChannelStat = (block: unknown): CustomerChannelStat | undefined => {
  const rec = asRecord(block)
  if (!rec) return undefined
  const orders = firstNum(rec, [
    'number_orders',
    'numberOrders',
    'number_of_orders',
    'orders',
  ])
  const spend = firstNum(rec, [
    'total_amount_spent',
    'totalAmountSpent',
    'total_spent',
    'amount',
  ])
  if (orders === undefined && spend === undefined) return undefined
  return { orders: orders ?? 0, spend: spend ?? 0 }
}

const mapChannelSplit = (raw: unknown): CustomerChannelSplit | undefined => {
  const root = asRecord(
    asRecord(raw)?.completed_order_last_12_months ??
      asRecord(raw)?.completedOrderLast12Months,
  )
  if (!root) return undefined
  const online = mapChannelStat(root.online_orders ?? root.onlineOrders)
  const inStore = mapChannelStat(root.pos_orders ?? root.posOrders)
  if (!online && !inStore) return undefined
  return {
    online: online ?? { orders: 0, spend: 0 },
    inStore: inStore ?? { orders: 0, spend: 0 },
  }
}

const mapComparison = (raw: unknown): Record<string, number> | undefined => {
  const rec = asRecord(
    asRecord(raw)?.average_comparison ?? asRecord(raw)?.averageComparison,
  )
  if (!rec) return undefined
  const out: Record<string, number> = {}
  for (const key of Object.keys(rec)) {
    const n = numOrUndef(rec[key])
    if (n !== undefined) out[key] = n
  }
  return Object.keys(out).length ? out : undefined
}

export const mapInsightsExtras = (raw: unknown): CustomerInsightsExtras => ({
  ordersLast30Days: firstNum(asRecord(raw), [
    'orders_last_30_days',
    'ordersLast30Days',
  ]),
  channelSplit: mapChannelSplit(raw),
  averageComparison: mapComparison(raw),
})
